'use client';

import { contactConfig } from '@/features/contact/contact.config';
import { Button } from '@embira/ui/components/button';
import { Input } from '@embira/ui/components/input';
import { useT } from 'next-i18next/client';
import type { FC } from 'react';

const inputClass =
  'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#F24E1E] focus-visible:ring-0 rounded-[6px]';

export const Contact: FC = () => {
  const { t } = useT(contactConfig.i18nNamespaces);

  return (
    <section className="bg-[#0A0A0A] py-24 md:py-[96px]">
      <div className="container">
        <div className="text-center mb-16">
          <p className="text-xs font-medium tracking-widest uppercase text-[#F24E1E]">
            {t('contact:form.label')}
          </p>
          <h2 className="mt-4 text-[clamp(36px,4vw,56px)] font-medium tracking-[-0.025em] leading-[1] text-white">
            {t('contact:form.title')}
          </h2>
          <p className="mt-6 text-base text-white/60 max-w-lg mx-auto leading-6">
            {t('contact:form.description')}
          </p>
        </div>

        <div className="max-w-2xl mx-auto border border-white/10 p-8 bg-white/5">
          <form action="#" method="POST">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="first-name" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.firstName')}
                </label>
                <Input
                  type="text"
                  id="first-name"
                  name="first-name"
                  autoComplete="given-name"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="last-name" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.lastName')}
                </label>
                <Input
                  type="text"
                  id="last-name"
                  name="last-name"
                  autoComplete="family-name"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.email')}
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.company')}
                </label>
                <Input
                  type="text"
                  id="company"
                  name="company"
                  autoComplete="organization"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="phone-number" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.phoneNumber')}
                </label>
                <Input
                  type="tel"
                  id="phone-number"
                  name="phone-number"
                  placeholder={t('contact:form.phonePlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.budget')}
                </label>
                <select
                  id="budget"
                  name="budget"
                  className="w-full h-9 px-3 py-1 text-sm rounded-[6px] border bg-white/5 border-white/10 text-white focus:outline-none focus:border-[#F24E1E]"
                >
                  <option value="0-10k" className="bg-[#0A0A0A]">0–10k</option>
                  <option value="10-50k" className="bg-[#0A0A0A]">10–50k</option>
                  <option value="50-100k" className="bg-[#0A0A0A]">50–100k</option>
                  <option value="100-150k" className="bg-[#0A0A0A]">100–150k</option>
                  <option value="150-250k" className="bg-[#0A0A0A]">150–250k</option>
                  <option value="250-500k" className="bg-[#0A0A0A]">250–500k</option>
                  <option value="500-750k" className="bg-[#0A0A0A]">500–750k</option>
                  <option value="750-1m" className="bg-[#0A0A0A]">750k–1m</option>
                  <option value="1m+" className="bg-[#0A0A0A]">1m+</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="message" className="block text-sm font-medium text-white/60 mb-2">
                  {t('contact:form.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  placeholder={t('contact:form.messagePlaceholder')}
                  className="w-full px-3 py-2 text-sm rounded-[6px] border bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-[#F24E1E] resize-none"
                />
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <Button
                type="submit"
                className="bg-[#111111] text-white rounded-[6px] px-7 hover:bg-[#F24E1E] transition-colors duration-300"
              >
                {t('contact:form.submit')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
